import { useCurrentUser } from '@/hooks/data/user';
import { openExternalCheckout } from '@/utils/client/externalCheckout';
import { SG_EVENT_NAMES } from '@/utils/tools/segment/eventNames';
import { trackEvent } from '@/utils/tools/segment/tracking';
import { Alert, Button, Space } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

export default function DownloadItemButton(props) {
   const { itemId, itemType, itemPremium, item } = props;
   const { user, error, isLoading } = useCurrentUser();
   const router = useRouter();
   const [downloadError, setDownloadError] = useState(null);
   const [downloadLoading, setDownloadLoading] = useState(false);
   const [autoDownloadTriggered, setAutoDownloadTriggered] = useState(false);
   const activeStatuses = ['active', 'trailing', 'past_due', 'paused'];

   const activeSubscription = (user?.subscriptions || []).find((subscription) =>
      activeStatuses.includes(String(subscription?.status || '').toLowerCase())
   );
   const activePlanName = String(activeSubscription?.subscriptionPlan?.name || '').toLowerCase();
   const isFreePlan = activePlanName === 'free';
   const hasVertexTag = (item?.tags || []).some((tag) => {
      const slug = String(tag?.slug || '').toLowerCase();
      const name = String(tag?.name || '').toLowerCase();
      return slug === 'vertex' || name === 'vertex';
   });
   const requiresCheckoutForFreeUser = !!user && (isFreePlan || !user?.isPremium) && (itemPremium || hasVertexTag);

   function userCanDownload(user) {
      if (itemPremium) {
         // check if user premium
         if (!user?.isPremium) return false;
         // check user download usage
         if (user?.downloadUsage >= user?.downloadLimit) return false;
      }
      if (!itemPremium) {
         // check user download usage
         if (user?.downloadUsage >= user?.downloadLimit) return false;
      }

      return true;
   }

   async function handleUpgradeAction() {
      if (downloadLoading) return;

      setDownloadError(null);
      setDownloadLoading(true);

      if (!user) {
         router.push(`/sign-up?redirect=${encodeURIComponent(router.asPath)}`);
         setDownloadLoading(false);
         return;
      }

      const checkoutResult = await openExternalCheckout({
         campaignTag: item?.slug || itemType || 'campaign',
         originPath: router.asPath || '/elements',
      });

      if (!checkoutResult.success) {
         setDownloadError({ message: checkoutResult.error });
      }
      setDownloadLoading(false);
   }

   const startDownload = useCallback(async ({ checkoutSessionId } = {}) => {
      setDownloadLoading(true);
      setDownloadError(null);
      let downloadUrl = '';
      try {
         const endpoint = checkoutSessionId ? '/api/items/unlocked-download' : '/api/items/download';
         const query = new URLSearchParams({
            assetType: itemType,
            itemId: String(itemId),
         });
         if (checkoutSessionId) {
            query.set('session_id', checkoutSessionId);
            query.set('campaignTag', item?.slug || itemType || 'campaign');
         }

         const response = await fetch(`${endpoint}?${query.toString()}`).then(async (response) => {
            if (!response.ok) {
               const error = new Error('An error occurred while downloading the file.');
               // get error message from body or default to response status
               error.info = await response.json();
               error.status = response.status;
               throw error;
            }
            return response.json();
         });
         downloadUrl = response.downloadFile;
      } catch (error) {
         setDownloadError(error);
         setDownloadLoading(false);
      }

      if (downloadUrl) {
         // download file from url in the same page
         const link = document.createElement('a');
         link.href = downloadUrl;
         link.setAttribute('download', 'file');
         document.body.appendChild(link);
         link.click();
         link.remove();
         trackEvent(SG_EVENT_NAMES.USER_DOWNLOAD_ITEM, {
            from: 'web',
            itemId,
            itemType,
            itemPremium,
            categories: item?.categories.map((c) => c.slug),
            author: item?.author_information?.email,
         });

         setDownloadLoading(false);
      }
   }, [itemId, itemType, itemPremium, item]);

   useEffect(() => {
      if (!router.isReady || autoDownloadTriggered || isLoading) return;

      const paymentStatus = String(router.query.payment || '').toLowerCase();
      const checkoutSessionId = String(router.query.session_id || '');
      if (paymentStatus !== 'success' || !checkoutSessionId) return;

      const handlePaidDownload = async () => {
         setAutoDownloadTriggered(true);
         setDownloadError(null);

         try {
            await startDownload({ checkoutSessionId });
         } catch (error) {
            setDownloadError({ message: error.message || 'Payment verification failed' });
         } finally {
            const restQuery = { ...router.query };
            delete restQuery.payment;
            delete restQuery.session_id;
            router.replace(
               {
                  pathname: router.pathname,
                  query: restQuery,
               },
               undefined,
               { shallow: true }
            );
         }
      };

      handlePaidDownload();
   }, [router, autoDownloadTriggered, isLoading, item?.slug, startDownload]);

   if (isLoading) return <p>Loading...</p>;

   if (error && error.status !== 401) return <p>Error: {error.message}</p>;

   const overLimit = user?.downloadUsage >= user?.downloadLimit;
   const requiresPremium = itemPremium && !user?.isPremium;
   const canDownload = user && userCanDownload(user) && !requiresCheckoutForFreeUser;
   const shouldUseCheckout = !user || requiresPremium || requiresCheckoutForFreeUser;

   return (
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
         {canDownload && (
            <Button
               type="primary"
               size="large"
               icon={<i className="fi fi-rr-download" />}
               onClick={startDownload}
               loading={downloadLoading}
               disabled={downloadLoading}
            >
               {downloadLoading ? 'Downloading...' : 'Download'}
            </Button>
         )}

         {!canDownload && shouldUseCheckout && (
            <Button
               type="primary"
               size="large"
               icon={<i className="fi fi-rr-lock" />}
               onClick={handleUpgradeAction}
               loading={downloadLoading}
               disabled={downloadLoading}
            >
               Unlock & Download
            </Button>
         )}

         {!canDownload && !shouldUseCheckout && overLimit && (
            <Button type="primary" size="large" icon={<i className="fi fi-rr-lock" />}>
               <Link href="/upgrade">Upgrade to download</Link>
            </Button>
         )}

         {downloadError && <Alert type="error" message={downloadError.info?.message || downloadError.message} showIcon />}
      </Space>
   );
}
